import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ArrowLeft, CircleCheck, ClipboardPlus, RotateCcw, XCircle } from 'lucide-react';
import { api } from '../../api';
import { pode as podeFazer, useSessao } from '../../sessao';
import { Modal } from '../../componentes/Modal';
import { NOME_PAPEL_CONTATO, telefone, tiposDe } from '../../componentes/formatos';
import { FormOs } from '../agenda/FormOs';
import { NOME_PRIORIDADE, NOME_RESULTADO, NOME_STATUS_OS, type ConfigAgenda, type Tecnico } from '../agenda/comum';
import { NOME_TIPO_CONTRATO } from '../contratos/comum';
import { EscolherLocal, type Escolha } from './EscolherLocal';
import { NOME_ORIGEM, NOME_STATUS_CHAMADO, idade, type ChamadoItem } from './comum';

interface Ficha extends ChamadoItem {
  ativo_id: string | null; ativo: string | null; contato_papel: string | null; contato_telefone: string | null;
  solucao: string | null; motivo_descarte: string | null; duplicado_de: string | null; duplicado_de_numero: string | null;
  resolvido_em: string | null; triado_em: string | null; aberto_por: string | null; triado_por: string | null; fechado_por: string | null;
  os: { id: string; numero: string; status: string; prioridade: string; agendada_para: string | null; fechada_em: string | null; resultado: string | null; tipo: string; responsavel: string | null }[];
  semelhantes: { id: string; numero: string; status: string; descricao: string; criado_em: string; local: string | null }[];
  contrato: { id: string; numero: string; tipo: string; janela: string; prazo_min: number | null; contagem: string | null } | null;
}

const dataHora = (iso: string) => new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
const prazoTexto = (min: number, contagem: string | null) =>
  `${min % 60 === 0 ? `${min / 60} h` : `${min} min`}${contagem === 'util' ? ' úteis' : ''}`;

export function DetalheChamado() {
  const { id } = useParams();
  const navegar = useNavigate();
  const { usuario } = useSessao();
  const podeAtender = podeFazer(usuario, 'chamados.atender');
  const podeAbrirOs = podeFazer(usuario, 'os.abrir');
  const [c, setC] = useState<Ficha | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [modal, setModal] = useState<'os' | 'resolver' | 'descartar' | null>(null);
  const [duplicadoDe, setDuplicadoDe] = useState<{ id: string; numero: string } | null>(null);
  const [tecnicos, setTecnicos] = useState<Tecnico[]>([]);
  const [cfg, setCfg] = useState<ConfigAgenda | null>(null);

  const carregar = useCallback(() => { api<Ficha>(`/chamados/${id}`).then(setC).catch((e) => setErro(e.message)); }, [id]);
  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    if (!podeAbrirOs) return;
    api<Tecnico[]>('/tecnicos').then(setTecnicos).catch(() => {});
    api<ConfigAgenda>('/agenda/config').then(setCfg).catch(() => {});
  }, [podeAbrirOs]);

  if (erro && !c) return <p className="alerta" role="alert">{erro}</p>;
  if (!c) return <p className="nota">Carregando…</p>;

  const aberto = ['novo', 'triado', 'em_atendimento'].includes(c.status);
  const semOs = ['novo', 'triado'].includes(c.status);
  const editavel = podeAtender && semOs;
  const fechar = (nova?: Ficha) => { setModal(null); setDuplicadoDe(null); if (nova) setC(nova); else carregar(); };

  async function reabrir() {
    try { setC(await api<Ficha>(`/chamados/${c!.id}/reabrir`, { metodo: 'POST', corpo: { versao: c!.versao } })); }
    catch (e) { setErro(e instanceof Error ? e.message : 'Não foi possível reabrir.'); }
  }

  return (
    <>
      <Link to="/chamados" className="voltar"><ArrowLeft size={16} aria-hidden /> Chamados</Link>
      <div className="cabecalho-pagina">
        <div>
          <span className="kicker">{NOME_ORIGEM[c.origem]}</span>
          <h1 className="titulo mono">{c.numero}</h1>
          <div className="meta">
            <span className={`status ch-${c.status}`}>{NOME_STATUS_CHAMADO[c.status]}</span>
            <span className={`pill-prioridade p-${c.prioridade}`}>{NOME_PRIORIDADE[c.prioridade]}</span>
            <span>chegou {idade(c.criado_em)}{c.aberto_por ? ` · registrado por ${c.aberto_por}` : ''}</span>
          </div>
        </div>
        {podeAtender && (
          <div className="acoes-cabecalho">
            {aberto && podeAbrirOs && <button className="btn-fantasma" onClick={() => setModal('os')}><ClipboardPlus size={16} aria-hidden /> Abrir OS</button>}
            {semOs && <button className="btn-fantasma" onClick={() => setModal('resolver')}><CircleCheck size={16} aria-hidden /> Resolver sem OS</button>}
            {semOs && <button className="btn-fantasma perigo" onClick={() => setModal('descartar')}><XCircle size={16} aria-hidden /> Descartar</button>}
            {!aberto && <button className="btn-fantasma" onClick={reabrir}><RotateCcw size={16} aria-hidden /> Reabrir</button>}
          </div>
        )}
      </div>

      {erro && <p className="alerta" role="alert">{erro}</p>}
      {c.status === 'novo' && <p className="alerta alerta-aviso">Sem triagem: diga de qual cliente e parque é o pedido para abrir OS ou resolver.</p>}

      <div className="grade-ficha">
        <section className="cartao ficha">
          <h2>Pedido</h2>
          <p className="texto-longo">{c.descricao}</p>
          <dl>
            <div><dt>Quem pediu</dt><dd>{c.contato
              ? <>{c.contato}{c.contato_papel ? ` · ${NOME_PAPEL_CONTATO[c.contato_papel]}` : ''} <span className="nota">(contato cadastrado)</span></>
              : c.solicitante_nome ?? '—'}</dd></div>
            <div><dt>Telefone</dt><dd>{c.solicitante_telefone ? <a href={`tel:+${c.solicitante_telefone}`}>{telefone(c.solicitante_telefone)}</a> : c.contato_telefone ? telefone(c.contato_telefone) : '—'}</dd></div>
            <div><dt>Chegou</dt><dd>{dataHora(c.criado_em)}</dd></div>
            {c.ativo && <div><dt>Equipamento</dt><dd><Link to={`/ativos/${c.ativo_id}`}>{c.ativo}</Link></dd></div>}
            {c.triado_em && <div><dt>Triado</dt><dd>{dataHora(c.triado_em)}{c.triado_por ? ` por ${c.triado_por}` : ''}</dd></div>}
          </dl>
        </section>

        <Triagem c={c} editavel={editavel} aoSalvar={fechar} />
      </div>

      {!aberto && (
        <section className="cartao ficha secao">
          <h2>{c.status === 'resolvido' ? 'Resolvido' : 'Descartado'}</h2>
          {c.status === 'resolvido' && <p className="texto-longo">{c.solucao ?? 'Resolvido pela OS abaixo.'}</p>}
          {c.status === 'descartado' && (c.duplicado_de
            ? <p className="texto-longo">Duplicado de <Link to={`/chamados/${c.duplicado_de}`}>{c.duplicado_de_numero}</Link>
                {c.motivo_descarte && !c.motivo_descarte.startsWith('Duplicado de') ? ` — ${c.motivo_descarte}` : ''}</p>
            : <p className="texto-longo">{c.motivo_descarte}</p>)}
          <small className="nota">{c.resolvido_em ? dataHora(c.resolvido_em) : dataHora(c.atualizado_em)}{c.fechado_por ? ` · ${c.fechado_por}` : ''}</small>
        </section>
      )}

      {c.semelhantes.length > 0 && (
        <section className="secao">
          <h2>Pedidos parecidos em aberto <span className="contador">{c.semelhantes.length}</span></h2>
          <p className="nota">Mesmo parque ou mesmo telefone. Se for o mesmo problema, marque este como duplicado e siga pelo outro.</p>
          <ul className="lista-simples cartao">
            {c.semelhantes.map((s) => (
              <li key={s.id}>
                <span><Link to={`/chamados/${s.id}`} className="mono">{s.numero}</Link> <small>{s.local ?? ''} · {s.descricao}</small></span>
                <span>{idade(s.criado_em)}</span>
                {podeAtender && semOs
                  ? <button className="btn-link" onClick={() => { setDuplicadoDe({ id: s.id, numero: s.numero }); setModal('descartar'); }}>é o mesmo</button>
                  : <span className={`status ch-${s.status}`}>{NOME_STATUS_CHAMADO[s.status]}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="secao">
        <h2>Ordens de serviço <span className="contador">{c.os.length}</span></h2>
        {c.os.length === 0 ? <p className="nota">Nenhuma OS ainda. O chamado fecha sozinho quando a última OS for concluída.</p> : (
          <ul className="lista-simples cartao">
            {c.os.map((o) => (
              <li key={o.id}>
                <span><Link to={`/os/${o.id}`} className="mono">{o.numero}</Link> <small>{o.tipo}{o.responsavel ? ` · ${o.responsavel}` : ''}</small></span>
                <span>{o.fechada_em ? dataHora(o.fechada_em) : o.agendada_para ? dataHora(o.agendada_para) : 'a agendar'}{o.resultado ? ` · ${NOME_RESULTADO[o.resultado]}` : ''}</span>
                <span className={`status st-${o.status}`}>{NOME_STATUS_OS[o.status]}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Modal titulo={`Abrir OS do ${c.numero}`} aberto={modal === 'os'} aoFechar={() => setModal(null)}>
        {modal === 'os' && <FormOs tecnicos={tecnicos} duracaoPadrao={cfg?.duracao_padrao_min ?? 60}
          chamado={{ id: c.id, numero: c.numero, cliente_id: c.cliente_id, cliente: c.cliente, local_id: c.local_id, descricao: c.descricao, prioridade: c.prioridade }}
          aoSalvar={(r) => { setModal(null); navegar(`/os/${r.id}`); }} aoCancelar={() => setModal(null)} />}
      </Modal>
      <Modal titulo="Resolver sem OS" aberto={modal === 'resolver'} aoFechar={() => setModal(null)}>
        {modal === 'resolver' && <FormFechar c={c} tipo="resolver" aoSalvar={fechar} aoCancelar={() => setModal(null)} />}
      </Modal>
      <Modal titulo={duplicadoDe ? `Duplicado de ${duplicadoDe.numero}` : 'Descartar chamado'} aberto={modal === 'descartar'} aoFechar={() => fechar()}>
        {modal === 'descartar' && <FormFechar c={c} tipo="descartar" duplicadoDe={duplicadoDe} aoSalvar={fechar} aoCancelar={() => fechar()} />}
      </Modal>
    </>
  );
}

function Triagem({ c, editavel, aoSalvar }: { c: Ficha; editavel: boolean; aoSalvar: (f: Ficha) => void }) {
  const [onde, setOnde] = useState<Escolha>({ cliente_id: c.cliente_id, cliente: c.cliente, local_id: c.local_id });
  const [prioridade, setPrioridade] = useState(c.prioridade);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  useEffect(() => { setOnde({ cliente_id: c.cliente_id, cliente: c.cliente, local_id: c.local_id }); setPrioridade(c.prioridade); }, [c]);
  const mudou = onde.cliente_id !== c.cliente_id || onde.local_id !== c.local_id || prioridade !== c.prioridade;

  async function salvar(e: FormEvent) {
    e.preventDefault(); setErro(null); setSalvando(true);
    try {
      const corpo: Record<string, unknown> = { versao: c.versao, prioridade };
      if (onde.cliente_id !== c.cliente_id) corpo.cliente_id = onde.cliente_id;
      if (onde.local_id !== c.local_id) corpo.local_id = onde.local_id;
      aoSalvar(await api<Ficha>(`/chamados/${c.id}`, { metodo: 'PATCH', corpo }));
    } catch (err) { setErro(err instanceof Error ? err.message : 'Não foi possível salvar.'); }
    finally { setSalvando(false); }
  }

  const contrato = c.contrato && (
    <div><dt>Contrato</dt><dd><Link to={`/contratos/${c.contrato.id}`}>{c.contrato.numero}</Link> · {NOME_TIPO_CONTRATO[c.contrato.tipo] ?? c.contrato.tipo}
      <small className="nota"> · {c.contrato.prazo_min ? `chegada em ${prazoTexto(c.contrato.prazo_min, c.contrato.contagem)} para prioridade ${NOME_PRIORIDADE[c.prioridade]?.toLowerCase()}` : 'sem prazo para esta prioridade'}</small></dd></div>
  );

  if (!editavel) {
    return (
      <section className="cartao ficha">
        <h2>Onde</h2>
        <dl>
          <div><dt>Cliente</dt><dd>{c.cliente_id ? <Link to={`/clientes/${c.cliente_id}`}>{c.cliente}</Link> : '—'}</dd></div>
          <div><dt>Parque</dt><dd>{c.local ?? '—'}</dd></div>
          {contrato ?? <div><dt>Contrato</dt><dd>{c.local_id ? 'Sem contrato que cubra este local (avulso)' : '—'}</dd></div>}
        </dl>
      </section>
    );
  }
  return (
    <section className="cartao ficha">
      <h2>Triagem</h2>
      <form className="form" onSubmit={salvar}>
        <EscolherLocal valor={onde} aoMudar={setOnde} />
        <label>Prioridade
          <select value={prioridade} onChange={(e) => setPrioridade(e.target.value)}>{tiposDe(NOME_PRIORIDADE).map(([v, r]) => <option key={v} value={v}>{r}</option>)}</select>
        </label>
        <dl>{contrato ?? <div><dt>Contrato</dt><dd>{c.local_id ? 'Sem contrato que cubra este parque (avulso)' : 'Escolha o parque para ver o contrato e o prazo'}</dd></div>}</dl>
        {erro && <p className="alerta" role="alert">{erro}</p>}
        {mudou && <div className="acoes-form"><button className="btn" disabled={salvando}>{salvando ? 'Salvando…' : 'Salvar triagem'}</button></div>}
      </form>
    </section>
  );
}

function FormFechar({ c, tipo, duplicadoDe, aoSalvar, aoCancelar }: {
  c: Ficha; tipo: 'resolver' | 'descartar'; duplicadoDe?: { id: string; numero: string } | null; aoSalvar: (f: Ficha) => void; aoCancelar: () => void;
}) {
  const [texto, setTexto] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const minimo = tipo === 'resolver' ? 10 : duplicadoDe ? 0 : 5;

  async function enviar(e: FormEvent) {
    e.preventDefault(); setErro(null); setSalvando(true);
    try {
      const corpo = tipo === 'resolver' ? { versao: c.versao, solucao: texto.trim() }
        : { versao: c.versao, ...(texto.trim() ? { motivo: texto.trim() } : {}), ...(duplicadoDe ? { duplicado_de: duplicadoDe.id } : {}) };
      aoSalvar(await api<Ficha>(`/chamados/${c.id}/${tipo}`, { metodo: 'POST', corpo }));
    } catch (err) { setErro(err instanceof Error ? err.message : 'Não foi possível salvar.'); }
    finally { setSalvando(false); }
  }

  return (
    <form className="form" onSubmit={enviar}>
      {tipo === 'resolver' && c.status === 'novo' && <p className="alerta alerta-aviso">Faça a triagem (cliente e parque) antes de resolver.</p>}
      <label>{tipo === 'resolver' ? 'O que foi feito' : duplicadoDe ? 'Observação (opcional)' : 'Por que descartar'}
        <textarea rows={4} maxLength={tipo === 'resolver' ? 4000 : 1000} value={texto} onChange={(e) => setTexto(e.target.value)} autoFocus
          placeholder={tipo === 'resolver' ? 'Ex.: "Acessado por TeamViewer, DVR reiniciado, imagens voltaram."' : 'Ex.: "Ligação por engano", "Serviço de outra empresa".'} />
      </label>
      {erro && <p className="alerta" role="alert">{erro}</p>}
      <div className="acoes-form">
        <button type="button" className="btn-fantasma" onClick={aoCancelar}>Voltar</button>
        <button className="btn" disabled={salvando || texto.trim().length < minimo}>
          {salvando ? 'Salvando…' : tipo === 'resolver' ? 'Marcar como resolvido' : duplicadoDe ? 'Marcar como duplicado' : 'Descartar'}</button>
      </div>
    </form>
  );
}
